import { useState, useEffect, useRef } from 'react'
import { Mic, MicOff, Edit3, Check } from 'lucide-react'
import { CATEGORIES, PRIORITIES } from '../../db/database'
import { Icon } from '../../ui/icons'
import { useT, useLang } from '../../i18n/useT'
import { useFeature } from '../../config/features'
import useTaskStore from '../../store/taskStore'
import useAuthStore from '../../store/authStore'
import useSmartListStore from '../../store/smartListStore'
import { stripPastDate } from './helpers'

const SpeechRecognitionAPI =
  typeof window !== 'undefined'
    ? (window.SpeechRecognition || window.webkitSpeechRecognition)
    : null

export default function VoiceMode({ onSave, onClose, onEditResult }) {
  const t                = useT()
  const lang             = useLang()
  const can              = useFeature()
  const openPremiumModal = useTaskStore(s => s.openPremiumModal)
  const authLoading      = useAuthStore(s => s.loading)

  const [phase,    setPhase]    = useState(() => SpeechRecognitionAPI ? 'idle' : 'unsupported')
  const [transcript, setTranscript] = useState('')
  const [interim,    setInterim]    = useState('')
  const [parsed,   setParsed]   = useState(null)
  const [errorMsg, setErrorMsg] = useState('')
  const [saving,   setSaving]   = useState(false)

  const recRef           = useRef(null)
  const timeoutRef       = useRef(null)
  const silenceRef       = useRef(null)
  const transcriptRef    = useRef('')
  const hadErrorRef      = useRef(false)

  useEffect(() => {
    if (authLoading) return // wait for auth to resolve
    if (!can('voice_input')) {
      openPremiumModal()
      onClose()
    }
  }, [authLoading]) // eslint-disable-line

  const parseWithServer = async (text) => {
    setPhase('processing')
    try {
      const token = useAuthStore.getState().session?.access_token
      if (!token) throw new Error('auth')

      const res = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/voice-to-task`,
        {
          method:  'POST',
          headers: {
            'Content-Type':  'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({ text }),
        }
      )
      const json = await res.json()

      if (res.status === 403) {
        openPremiumModal()
        onClose()
        return
      }
      if (!res.ok) throw new Error(json.error || 'server')

      setParsed({ ...json, transcript: text })
      setPhase('done')
    } catch (e) {
      console.error('[VoiceMode]', e)
      setErrorMsg(e.message === 'auth' ? t('voice_err_auth') : t('voice_err_network'))
      setPhase('error')
    }
  }

  const startListening = () => {
    transcriptRef.current = ''
    hadErrorRef.current   = false
    setTranscript('')
    setInterim('')
    setErrorMsg('')

    const isAndroid = /Android/i.test(navigator.userAgent)
    const rec = new SpeechRecognitionAPI()
    rec.lang             = lang === 'it' ? 'it-IT' : 'en-US'
    rec.continuous       = !isAndroid  // Android continuous mode is buggy
    rec.interimResults   = true
    rec.maxAlternatives  = 1
    recRef.current       = rec

    rec.onstart = () => {
      setPhase('listening')
      timeoutRef.current = setTimeout(() => rec.stop(), 30_000)
      silenceRef.current = setTimeout(() => rec.stop(), 4_000)
    }

    rec.onresult = (e) => {
      clearTimeout(silenceRef.current)
      silenceRef.current = setTimeout(() => rec.stop(), 4_000)

      if (isAndroid) {
        // Android: continuous=false, last result only — no duplication
        const last = e.results[e.results.length - 1]
        const text = last[0].transcript.trim()
        if (last.isFinal) {
          transcriptRef.current = text
          setTranscript(text)
          setInterim('')
        } else {
          setInterim(text)
        }
        return
      }

      // Desktop/iOS: continuous=true, accumulate from resultIndex
      let newFinal = ''
      let lastInterim = ''
      for (let i = e.resultIndex; i < e.results.length; i++) {
        if (e.results[i].isFinal) {
          newFinal += e.results[i][0].transcript
        } else {
          lastInterim = e.results[i][0].transcript
        }
      }

      // Accumulate confirmed text in the ref (not from e.results, to avoid Android duplication)
      if (newFinal) {
        transcriptRef.current = (transcriptRef.current + ' ' + newFinal).trim()
        setTranscript(transcriptRef.current)
        setInterim('')
      } else {
        setInterim(lastInterim.trim())
      }
    }

    rec.onerror = (e) => {
      hadErrorRef.current = true
      clearTimeout(timeoutRef.current)
      clearTimeout(silenceRef.current)
      if (e.error === 'aborted') {
        // User stopped manually — onend will handle it
        hadErrorRef.current = false
        return
      }
      const msgs = {
        'not-allowed':   t('voice_err_mic_denied'),
        'no-speech':     t('voice_err_no_speech'),
        'network':       t('voice_err_network'),
        'audio-capture': t('voice_err_no_mic'),
      }
      setErrorMsg(msgs[e.error] ?? `Error: ${e.error}`)
      setPhase('error')
    }

    rec.onend = () => {
      clearTimeout(timeoutRef.current)
      clearTimeout(silenceRef.current)
      setInterim('')
      if (hadErrorRef.current) return
      const text = transcriptRef.current.trim()
      if (text) {
        parseWithServer(text)
      } else {
        setErrorMsg(t('voice_err_no_text'))
        setPhase('error')
      }
    }

    rec.start()
  }

  const stopListening = () => {
    clearTimeout(timeoutRef.current)
    clearTimeout(silenceRef.current)
    recRef.current?.stop()
  }

  // Auto-start recording on mount
  const [autoStarted, setAutoStarted] = useState(false)
  useEffect(() => {
    if (autoStarted) return
    if (phase === 'idle' && SpeechRecognitionAPI && can('voice_input')) {
      setAutoStarted(true)
      startListening()
    }
  }, [phase, autoStarted]) // eslint-disable-line

  const handleConfirm = async () => {
    setSaving(true)
    try {
      const clean = stripPastDate({
        title:           parsed.title,
        category:        parsed.category,
        dueDate:         parsed.dueDate ?? null,
        dueTime:         parsed.dueTime ?? null,
        priority:        parsed.priority,
        reminder:        parsed.reminder ?? false,
        reminderMinutes: parsed.reminderMinutes ?? 15,
        notes:           '',
      }, lang, t)
      const newId = await onSave(clean)
      // Smart list auto-create: se l'AI ha estratto voci di checklist, crea la
      // lista e popola gli item. Best-effort: errori loggati ma non bloccano l'UX.
      const aiItems = Array.isArray(parsed.items)
        ? parsed.items.map(s => (typeof s === 'string' ? s.trim() : '')).filter(Boolean)
        : []
      if (newId && aiItems.length > 0) {
        try {
          const sls = useSmartListStore.getState()
          await sls.createList(newId)
          for (const text of aiItems.slice(0, 20)) {
            await sls.addItem(newId, { text })
          }
        } catch (e) { console.warn('[VoiceMode] smart list create failed', e) }
      }
      onClose()
    } catch {
      setSaving(false)
    }
  }

  const reset = () => {
    setPhase('idle')
    setTranscript('')
    setInterim('')
    setParsed(null)
    setErrorMsg('')
    hadErrorRef.current   = false
    transcriptRef.current = ''
  }

  return (
    <div className="voice-modal">

      {phase === 'idle' && (
        <>
          <button className="voice-start-btn" onClick={startListening}>
            <Mic size={32} />
          </button>
          <div className="voice-title" style={{ marginTop: 24 }}>{t('voice_title')}</div>
          <div className="voice-subtitle" style={{ marginTop: 6 }}>{t('voice_speak')}</div>
          <div className="voice-hint">{t('voice_hint')}</div>
        </>
      )}

      {phase === 'listening' && (
        <>
          <div className="voice-rings">
            <div className="voice-ring" />
            <div className="voice-ring" />
            <div className="voice-ring" />
            <button className="voice-mic-btn" onClick={stopListening} title={t('voice_stop')}>
              <Mic size={28} />
            </button>
          </div>
          <div className="voice-live-text">
            {(transcript || interim) ? (
              <>{transcript}{interim && <span style={{ opacity: 0.5 }}>{interim}</span>}</>
            ) : (
              <span style={{ opacity: 0.5 }}>{t('voice_listening')}</span>
            )}
          </div>
          <button className="btn btn-ghost" onClick={stopListening} style={{ marginTop: 16, width: '100%' }}>
            {t('voice_stop')}
          </button>
        </>
      )}

      {phase === 'processing' && (
        <>
          <div className="voice-processing-spinner" />
          <div className="voice-title" style={{ marginTop: 24 }}>{t('voice_analyzing')}</div>
          <div className="voice-transcript" style={{ marginTop: 12 }}>
            "{transcriptRef.current}"
          </div>
        </>
      )}

      {phase === 'done' && parsed && (
        <>
          <div className="voice-result-icon"><Check size={36} strokeWidth={3} /></div>
          <div className="voice-transcript">"{parsed.transcript}"</div>

          <div className="voice-chips">
            {parsed.category && CATEGORIES[parsed.category] && (() => {
              const C = CATEGORIES[parsed.category]
              return (
                <span className="voice-chip">
                  <C.Icon size={12} strokeWidth={2.2} />
                  {C.label}
                </span>
              )
            })()}
            {parsed.dueDate && (
              <span className="voice-chip">
                <Icon.Calendar size={12} />
                {new Intl.DateTimeFormat(lang === 'it' ? 'it-IT' : 'en-GB', {
                  day: 'numeric', month: 'short'
                }).format(new Date(parsed.dueDate + 'T12:00:00'))}
                {parsed.dueTime && ` ${parsed.dueTime}`}
              </span>
            )}
            {parsed.priority && parsed.priority !== 'media' && (
              <span className="voice-chip" style={{ color: PRIORITIES[parsed.priority]?.color }}>
                {PRIORITIES[parsed.priority]?.label}
              </span>
            )}
            {parsed.reminder && (
              <span className="voice-chip">
                <Icon.Bell size={12} />
                {parsed.reminderMinutes || 15} min
              </span>
            )}
          </div>

          <div className="voice-done-actions">
            <div style={{ display: 'flex', gap: 8, width: '100%' }}>
              <button className="btn btn-ghost" onClick={reset} style={{ flex: 1 }}>
                <MicOff size={15} /> {t('voice_retry')}
              </button>
              <button className="btn btn-ghost" onClick={() => onEditResult(parsed)} style={{ flex: 1 }}>
                <Edit3 size={15} /> {t('voice_edit')}
              </button>
            </div>
            <button className="btn btn-primary" onClick={handleConfirm} disabled={saving} style={{ width: '100%' }}>
              <Check size={15} /> {saving ? '…' : t('voice_add')}
            </button>
          </div>
        </>
      )}

      {phase === 'error' && (
        <>
          <div style={{ fontSize: '2.5rem', marginBottom: 12, textAlign: 'center' }}>⚠️</div>
          <div className="voice-title" style={{ color: 'var(--danger, #e74c3c)', marginBottom: 8, textAlign: 'center' }}>
            {t('voice_error')}
          </div>
          <div className="voice-subtitle" style={{ textAlign: 'center', whiteSpace: 'pre-line' }}>
            {errorMsg}
          </div>
          <button className="btn btn-primary" onClick={reset} style={{ marginTop: 24, width: '100%' }}>
            {t('voice_retry')}
          </button>
        </>
      )}

      {phase === 'unsupported' && (
        <>
          <Icon.Mic size={40} style={{ marginBottom: 12, color: 'var(--text-3)' }} />
          <div className="voice-title">{t('voice_unsupported')}</div>
          <div className="voice-subtitle" style={{ textAlign: 'center', marginTop: 8 }}>
            {t('voice_unsupported_msg')}
          </div>
          <button className="btn btn-ghost" onClick={onClose} style={{ marginTop: 24, width: '100%' }}>
            {t('voice_close')}
          </button>
        </>
      )}

    </div>
  )
}
